import { ageEnAnnees, type CouleurAvatar, type SilhouetteAvatar } from '@repere/core';
import { useEffect, useState } from 'react';
import { useSession } from '../auth/session';

/**
 * L'apparence d'un avatar, telle que l'API la rend avec chaque enfant.
 *
 * Elle **ne porte pas** l'identifiant de l'enfant : un avatar appartient à un enfant, et
 * le lui faire répéter a déjà produit un `/children/undefined/avatar/photo` que rien ne
 * signalait. L'appelant, qui tient l'enfant, passe l'identifiant séparément.
 */
export interface AvatarEnfant {
  silhouette: SilhouetteAvatar;
  color: CouleurAvatar;
  hasPhoto: boolean;
}

/**
 * Les silhouettes, en trois dessins.
 *
 * Elles se distinguent par la coiffure seule — même corps, même posture, même épaisseur de
 * trait. Une « fille » plus petite ou aux formes différentes ferait dire au dessin ce
 * qu'aucun parent n'a demandé, sur un écran que deux familles regardent chaque jour.
 */
const SILHOUETTES: Record<SilhouetteAvatar, React.ReactNode> = {
  neutre: (
    <>
      <circle cx="24" cy="18" r="8" />
      <path d="M10 42c0-7.7 6.3-14 14-14s14 6.3 14 14" />
    </>
  ),
  fille: (
    <>
      <circle cx="24" cy="18" r="8" />
      {/* Cheveux longs : deux mèches de part et d'autre du visage. */}
      <path d="M15 15c0-5 4-9 9-9s9 4 9 9v3M15 15v9M33 15v9" />
      <path d="M10 42c0-7.7 6.3-14 14-14s14 6.3 14 14" />
    </>
  ),
  garcon: (
    <>
      <circle cx="24" cy="18" r="8" />
      {/* Cheveux courts : une frange posée sur le front. */}
      <path d="M16 14c2-3 5-4 8-4s6 1 8 4" />
      <path d="M10 42c0-7.7 6.3-14 14-14s14 6.3 14 14" />
    </>
  ),
};

/**
 * Avatar d'un enfant : sa photo, ou une silhouette colorée, et son âge posé dessus.
 *
 * **L'âge est calculé, jamais stocké.** Un âge en base est faux dès le lendemain de
 * l'anniversaire ; le rattraper demanderait une tâche planifiée là où une soustraction
 * suffit. Il est aussi ce qui rend l'avatar utile plutôt que décoratif : sur une carte de
 * dossier, il évite de calculer de tête à partir d'une date de naissance.
 *
 * **La photo passe par une requête authentifiée**, jamais par une adresse posée dans un
 * `src`. Un portrait d'enfant joignable par une URL qui circule, se copie et survit à la
 * session serait la pièce la plus exposée d'un dossier par ailleurs chiffré. Le prix est
 * qu'il faut fabriquer une URL d'objet et la libérer — c'est fait ici, une fois.
 *
 * En cas d'échec du chargement, la silhouette reprend sa place : un avatar cassé sur la
 * page d'accueil vaut moins qu'un dessin.
 *
 * **Il est décoratif pour un lecteur d'écran**, chiffre de l'âge compris. Partout où il
 * apparaît, le prénom et l'âge sont déjà écrits en toutes lettres à côté — sur la carte
 * d'accueil comme dans le sélecteur d'enfant. Les annoncer une seconde fois ferait
 * entendre « 8, Alba 8 ans, Alba » là où il faut lire « Alba ». Le `prenom` reste demandé
 * en propriété : il n'est pas rendu, mais il documente à quel enfant l'avatar appartient,
 * et un appel qui l'oublierait signalerait qu'on l'affiche hors de son contexte.
 */
export function Avatar({
  childId,
  avatar,
  prenom,
  naissance,
  taille = 56,
  maintenant,
}: {
  childId: string;
  avatar: AvatarEnfant;
  prenom: string;
  naissance: string;
  /** Diamètre en pixels. 56 sur une carte, 96 sur une fiche. */
  taille?: number;
  maintenant: Date;
}) {
  const { client } = useSession();
  const [photo, setPhoto] = useState<string | null>(null);

  useEffect(() => {
    if (!avatar.hasPhoto) {
      setPhoto(null);
      return;
    }

    let annule = false;
    let url: string | null = null;

    void client
      .requestBinary(`/children/${childId}/avatar/photo`)
      .then(({ bytes }) => {
        if (annule) return;
        url = URL.createObjectURL(bytes);
        setPhoto(url);
      })
      .catch((erreur: unknown) => {
        // La silhouette reprend sa place : un avatar cassé sur l'accueil vaut moins qu'un
        // dessin. Mais l'échec laisse une trace — un `catch` muet a déjà coûté une
        // demi-heure de diagnostic pour une photo qui ne s'affichait pas.
        if (import.meta.env.DEV) console.warn('[avatar] photo non chargée', erreur);
      });

    return () => {
      annule = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [client, childId, avatar.hasPhoto]);

  const age = ageEnAnnees(new Date(naissance), maintenant);

  return (
    <span
      className={`avatar avatar-${avatar.color}`}
      style={{ width: taille, height: taille }}
      data-testid="avatar"
      data-enfant={prenom}
      aria-hidden="true"
    >
      {photo ? (
        // `alt=""` : le prénom et l'âge sont déjà écrits à côté, et les répéter ferait
        // entendre deux fois la même chose au lecteur d'écran.
        <img src={photo} alt="" className="avatar-photo" />
      ) : (
        <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {SILHOUETTES[avatar.silhouette]}
        </svg>
      )}

      <span className="avatar-age" aria-hidden="true">
        {age}
      </span>
    </span>
  );
}
